import { ReactNode, useEffect } from 'react';
import { Box, Flex, HStack, Text, IconButton } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tg, useTelegramBackButton } from '../telegram';
import { BRAND } from '@algobot/shared';

/**
 * Algomed Mini App kabuğu.
 * - Telegram BackButton entegrasyonu (Home dışı route'larda)
 * - Sayfa değişiminde scroll restoration
 * - Slim 48px brand header (Algomed mark + sayfa başlığı + close)
 *   → Algomed Layout.jsx'in mobil header (`headerBg` + ortalanmış brand)
 *     desenini Telegram WebApp için sadeleştirilmiş hâli.
 * - iOS safe-area top inset.
 */
const TITLES: Record<string, string> = {
  '/': 'Algomed',
  '/connect': 'Hesap Bağla',
  '/subscription': 'Abonelik',
  '/question': 'Soru Çöz',
  '/flashcard': 'Flash Kart',
  '/algospot': 'AlgoSpot',
  '/trainer': 'Dijital Antrenör',
  '/mentor': 'Dijital Mentor',
  '/topics': 'Konu Evreni',
};

function pageTitleFor(pathname: string): string {
  if (TITLES[pathname]) return TITLES[pathname];
  if (pathname.startsWith('/question/')) return 'Soru';
  if (pathname.startsWith('/flashcard/')) return 'Flash Kart';
  if (pathname.startsWith('/algospot/')) return 'AlgoSpot';
  if (pathname.startsWith('/lecture/')) return 'Konu Anlatımı';
  if (pathname.startsWith('/topics')) return 'Konu Evreni';
  return 'Algomed';
}

export default function AppShell({ children }: { children: ReactNode }) {
  const nav = useNavigate();
  const loc = useLocation();
  const isHome = loc.pathname === '/';
  const platform = tg()?.platform || 'web';
  const isIos = platform === 'ios';

  useEffect(() => {
    if (isHome) return;
    const cleanup = useTelegramBackButton(() => nav(-1));
    return cleanup;
  }, [isHome, nav]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  const title = pageTitleFor(loc.pathname);

  return (
    <Box
      minH="100vh"
      bg="pageBackground"
      pt={isIos ? 'env(safe-area-inset-top)' : 0}
      pb={isIos ? 'env(safe-area-inset-bottom)' : 0}
    >
      <Flex
        as="header"
        position="sticky"
        top={isIos ? 'env(safe-area-inset-top)' : 0}
        zIndex={10}
        h="48px"
        align="center"
        justify="space-between"
        px={3}
        bg="headerBg"
        borderBottomWidth="1px"
        borderColor="borderPrimary"
        backdropFilter="saturate(180%) blur(20px)"
      >
        <HStack spacing={2} minW="60px">
          <Text fontSize="lg" lineHeight="1" color="accent" fontWeight="bold">
            {BRAND.emojiBrand}
          </Text>
          {isHome ? (
            <Text fontSize="sm" fontWeight="bold" color="textPrimary" noOfLines={1}>
              {BRAND.name}
            </Text>
          ) : null}
        </HStack>

        <Text
          fontSize="sm"
          fontWeight="semibold"
          color="textPrimary"
          noOfLines={1}
          flex="1"
          textAlign="center"
          px={2}
        >
          {isHome ? '' : title}
        </Text>

        <Flex minW="60px" justify="flex-end">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label="Kapat"
            onClick={() => tg()?.close()}
            icon={
              <Box as="span" fontSize="lg" lineHeight="1" color="textSecondary">
                ×
              </Box>
            }
          />
        </Flex>
      </Flex>

      <Box as="main">{children}</Box>
    </Box>
  );
}
